import { z } from 'zod';
import { pipe, Option, Array as A } from 'effect';

import type { PayingAccountField } from '@/domain/paySettings/schemas';
import { BBAN_BANK_CODE_REGEX, BBAN_BRANCH_CODE_REGEX, BBAN_ACCOUNT_NUMBER_REGEX } from '@/lib/bban';
import { assembleIsraeliIban, splitIsraeliIban } from '@/lib/iban';

import {
  readString,
  resolveComputedPart,
  type FormValues,
} from '@/pages/dokkaPay/pages/configurations/company/pages/paymentProviders/components/Providers/components/DialogPaymentProviderDetails/components/DialogAddGlobalAccount/components/PayingAccountForm/utils';

// Validation is not in the form description — it is keyed by field name here.
// Keys resolved by `helperTextError` as `message.<key>`.
const REQUIRED = 'required';

const FIELD_PATTERNS: Record<string, { pattern: RegExp; message: string }> = {
  psu_id: { pattern: /^\d{5,9}$/, message: 'invalid_psu_id' },
  branch_code: { pattern: BBAN_BRANCH_CODE_REGEX, message: 'invalid_branch_code' },
  bank_account_number: { pattern: BBAN_ACCOUNT_NUMBER_REGEX, message: 'invalid_account_number' },
};

const PSU_ID_FIELD = 'psu_id';
const PSU_ID_LENGTH = 9;

const fieldSchema = (field: PayingAccountField): z.ZodType<string, string> =>
  pipe(
    Option.fromNullable(FIELD_PATTERNS[field.name]),
    Option.match({
      onNone: () => z.string().min(1, REQUIRED),
      onSome: ({ pattern, message }) => z.string().min(1, REQUIRED).regex(pattern, message),
    }),
  );

// Hidden non-derived fields carry backend defaults and are not validated.
export const buildFormSchema = (
  fields: PayingAccountField[],
  isValidated: (field: PayingAccountField) => boolean = () => true,
) =>
  z.object(
    Object.fromEntries(
      pipe(
        fields,
        A.filter((field) => isValidated(field) && (field.type === 'iban' || !field.hidden)),
        A.map((field): [string, z.ZodType<string, string>] => [
          field.name,
          field.type === 'iban' ? z.string().min(1, REQUIRED) : fieldSchema(field),
        ]),
      ),
    ),
  );

// '' until every `computedFrom` part is valid, which blocks submission.
export const assembleIban = (
  field: PayingAccountField,
  fields: PayingAccountField[],
  values: Record<string, unknown>,
): string => {
  const [bankCode = '', branchCode = '', accountNumber = ''] = pipe(
    field.computedFrom ?? [],
    A.map((part) => resolveComputedPart(part, fields, values)),
  );

  const allPartsValid =
    BBAN_BANK_CODE_REGEX.test(bankCode) &&
    BBAN_BRANCH_CODE_REGEX.test(branchCode) &&
    BBAN_ACCOUNT_NUMBER_REGEX.test(accountNumber);

  return allPartsValid ? assembleIsraeliIban({ bankCode, branchCode, accountNumber }) : '';
};

// A single-value field may get a one-element list default (`['iban']`).
const defaultOf = (field: PayingAccountField): string =>
  Array.isArray(field.defaultValue) ? (field.defaultValue[0] ?? '') : (field.defaultValue ?? '');

const storedStrings = (stored: Record<string, unknown>): FormValues =>
  Object.fromEntries(
    pipe(
      Object.entries(stored),
      A.filter((entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].length > 0),
    ),
  );

// None unless exactly one option matches — bank codes are not unique.
const resolveOwnerValue = (fields: PayingAccountField[], attribute: string, attributeValue: string) =>
  pipe(
    fields,
    A.findFirst((field) => A.some(field.options ?? [], (option) => attribute in option)),
    Option.flatMap((owner) =>
      pipe(
        owner.options ?? [],
        A.filter((option) => option[attribute] === attributeValue),
        Option.liftPredicate((matches) => matches.length === 1),
        Option.flatMap(A.head),
        Option.map((match): [string, string] => [owner.name, match.value]),
      ),
    ),
  );

// Splits a stored IBAN into the part fields that are still empty.
const applyIbanDecomposition = (fields: PayingAccountField[], values: FormValues): FormValues =>
  pipe(
    fields,
    A.findFirst((field) => field.type === 'iban' && (field.computedFrom?.length ?? 0) === 3),
    Option.filter((iban) => readString(values, iban.name).length > 0),
    Option.map((iban) => {
      const parts = splitIsraeliIban(readString(values, iban.name));
      const partValues = [parts.bankCode, parts.branchCode, parts.accountNumber];
      const fieldNames = new Set(fields.map((field) => field.name));

      return pipe(
        iban.computedFrom ?? [],
        A.zip(partValues),
        A.reduce(values, (acc, [partName, partValue]) => {
          if (!partValue) return acc;
          if (fieldNames.has(partName)) {
            return readString(acc, partName) ? acc : { ...acc, [partName]: partValue };
          }
          return pipe(
            resolveOwnerValue(fields, partName, partValue),
            Option.filter(([ownerName]) => !readString(acc, ownerName)),
            Option.match({
              onNone: () => acc,
              onSome: ([ownerName, ownerValue]) => ({ ...acc, [ownerName]: ownerValue }),
            }),
          );
        }),
      );
    }),
    Option.getOrElse(() => values),
  );

const toSnakeCase = (key: string): string => key.replace(/[A-Z]/g, (char) => `_${char.toLowerCase()}`);

// Table rows are camelCase, form fields snake_case.
export const fromPayingAccountRow = (row: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(
    pipe(
      Object.entries(row),
      A.map(([key, value]): [string, unknown] => [toSnakeCase(key), value]),
    ),
  );

export const buildDefaultValues = (fields: PayingAccountField[], stored?: Record<string, unknown>): FormValues => {
  const defaults: FormValues = Object.fromEntries(
    pipe(
      fields,
      A.map((field): [string, string] => [field.name, defaultOf(field)]),
    ),
  );

  return pipe(
    Option.fromNullable(stored),
    Option.map((record) => applyIbanDecomposition(fields, { ...defaults, ...storedStrings(record) })),
    Option.getOrElse(() => defaults),
  );
};

export const toSubmitValues = (fields: PayingAccountField[], values: Record<string, unknown>): FormValues =>
  Object.fromEntries(
    pipe(
      fields,
      A.map((field): [string, string] => {
        const value = readString(values, field.name);
        return [field.name, field.name === PSU_ID_FIELD ? value.padStart(PSU_ID_LENGTH, '0') : value];
      }),
    ),
  );
